import React, { useEffect, useState, useCallback } from "react";
import { Table, Pagination } from "rsuite";
import { fetchProjects } from "../../../api/projectAPI";
import ImageModal from "../../imageModal";
import ReadMoreModal from "../../readMoreModal";
import { handleDeleteProject } from "./deleteProjectHandler";
import { placeholderImage, isValidUrl } from "../../invalidImage";
import AdminPageHeader from "../_shared/AdminPageHeader";
import AdminSearchBar from "../_shared/AdminSearchBar";
import AdminCard from "../_shared/AdminCard";
import { EditButton, DeleteButton } from "../_shared/AdminActions";
import ProjectEditor from "./ProjectEditor";

const { Column, HeaderCell, Cell } = Table;

// ── View modes ────────────────────────────────────────────────
const VIEW = {
  LIST: "list",
  ADD:  "add",
  EDIT: "edit",
};

const ProjectTable = () => {
  const [view, setView]               = useState(VIEW.LIST);
  const [editingProject, setEditingProject] = useState(null);

  const [allData, setAllData]         = useState([]);
  const [data, setData]               = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const [page, setPage]               = useState(1);
  const [limit, setLimit]             = useState(10);
  const [searchKeyword, setSearchKeyword] = useState("");

  const fetchData = useCallback(async () => {
    try {
      const projectsData = await fetchProjects();
      setAllData(projectsData);
      setData(projectsData);
    } catch (err) {
      console.error("Failed to fetch projects:", err);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const start = (page - 1) * limit;
    setDisplayData(data.slice(start, start + limit));
  }, [page, limit, data]);

  const handleSearch = (value) => {
    setSearchKeyword(value);
    setPage(1);
    if (value) {
      setData(allData.filter((p) => p.title.toLowerCase().includes(value.toLowerCase())));
    } else {
      setData(allData);
    }
  };

  const handleEditorSuccess = () => {
    setView(VIEW.LIST);
    setEditingProject(null);
    fetchData();
  };

  // ── Full-page editor view ─────────────────────────────────
  if (view === VIEW.ADD) {
    return (
      <div className="flex-1 min-h-screen">
        <ProjectEditor
          onSuccess={handleEditorSuccess}
          onCancel={() => setView(VIEW.LIST)}
        />
      </div>
    );
  }

  if (view === VIEW.EDIT && editingProject) {
    return (
      <div className="flex-1 min-h-screen">
        <ProjectEditor
          project={editingProject}
          onSuccess={handleEditorSuccess}
          onCancel={() => { setView(VIEW.LIST); setEditingProject(null); }}
        />
      </div>
    );
  }

  // ── List view ─────────────────────────────────────────────
  return (
    <div className="flex-1 min-h-screen bg-slate-50 overflow-y-auto">
      <div className="p-6 lg:p-10 max-w-7xl mx-auto">
        <AdminPageHeader
          title="Project Management"
          subtitle="Kelola portfolio project Defra"
          badge={data.length}
          actionLabel="➕ Tambah Project"
          onAction={() => setView(VIEW.ADD)}
          icon={
            <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          }
        />

        <AdminCard>
          <AdminSearchBar
            value={searchKeyword}
            onChange={handleSearch}
            placeholder="Cari berdasarkan judul project..."
          />

          <div className="overflow-hidden rounded-2xl border border-slate-100">
            <Table autoHeight data={displayData} rowHeight={100}>
              <Column width={60} align="center" fixed>
                <HeaderCell className="bg-slate-50 font-semibold text-slate-700">No</HeaderCell>
                <Cell>
                  {(rowData, rowIndex) => (
                    <span className="text-sm font-semibold text-slate-500">
                      {(page - 1) * limit + rowIndex + 1}
                    </span>
                  )}
                </Cell>
              </Column>

              <Column width={100}>
                <HeaderCell className="bg-slate-50 font-semibold text-slate-700">Image</HeaderCell>
                <Cell>
                  {(rowData) => (
                    <ImageModal imageUrl={isValidUrl(rowData.image) ? rowData.image : placeholderImage} />
                  )}
                </Cell>
              </Column>

              <Column width={280}>
                <HeaderCell className="bg-slate-50 font-semibold text-slate-700">Judul</HeaderCell>
                <Cell>
                  {(rowData) => (
                    <span className="font-semibold text-slate-800">{rowData.title}</span>
                  )}
                </Cell>
              </Column>

              <Column flexGrow={1} minWidth={300}>
                <HeaderCell className="bg-slate-50 font-semibold text-slate-700">Deskripsi</HeaderCell>
                <Cell>
                  {(rowData) => {
                    const text = rowData.description || "";
                    const truncated = text.length > 100 ? text.slice(0, 100) + "..." : text;
                    return (
                      <ReadMoreModal text={text}>
                        <span className="text-sm text-slate-600">{truncated}</span>
                      </ReadMoreModal>
                    );
                  }}
                </Cell>
              </Column>

              <Column width={200}>
                <HeaderCell className="bg-slate-50 font-semibold text-slate-700">Aksi</HeaderCell>
                <Cell>
                  {(rowData) => (
                    <div className="flex items-center gap-2">
                      <EditButton
                        onClick={() => {
                          setEditingProject(rowData);
                          setView(VIEW.EDIT);
                        }}
                      />
                      <DeleteButton onClick={() => handleDeleteProject(rowData.id, data, setData)} />
                    </div>
                  )}
                </Cell>
              </Column>
            </Table>
          </div>

          <div className="flex justify-end mt-5">
            <Pagination
              prev next first last ellipsis boundaryLinks
              maxButtons={5} size="xs"
              layout={["total", "-", "limit", "|", "pager"]}
              total={data.length}
              limitOptions={[5, 10, 20, 50]}
              limit={limit}
              activePage={page}
              onChangePage={setPage}
              onChangeLimit={(l) => { setLimit(l); setPage(1); }}
            />
          </div>
        </AdminCard>
      </div>
    </div>
  );
};

export default ProjectTable;
